import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { CreditCard, Receipt, BarChart3, BookCopy } from "lucide-react";
import { SubscriptionTab } from "./SubscriptionTab";
import { PlansTab } from "./PlansTab";
import { InvoicesTab } from "./InvoicesTab";
import { UsageTab } from "./UsageTab";

import { PaymentsList } from "./PaymentsList";

type BillingTab = "subscription" | "plans" | "invoices" | "payments" | "usage";

interface TabConfig {
  id: BillingTab;
  label: string;
  icon: React.ElementType;
  description: string;
}

const tabs: TabConfig[] = [
  {
    id: "subscription",
    label: "Subscription",
    icon: CreditCard,
    description: "Manage your current plan",
  },
  {
    id: "plans",
    label: "Plans",
    icon: BookCopy,
    description: "View available plans",
  },
  {
    id: "invoices",
    label: "Invoices",
    icon: Receipt,
    description: "Billing history",
  },
  {
    id: "payments",
    label: "Payments",
    icon: CreditCard,
    description: "Top-up history",
  },
  {
    id: "usage",
    label: "Usage",
    icon: BarChart3,
    description: "Credit consumption",
  },
];

export default function BillingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<BillingTab>("subscription");

  // Initialize tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["subscription", "plans", "invoices", "payments", "usage"].includes(tabParam)) {
      setActiveTab(tabParam as BillingTab);
    }
  }, [searchParams]);

  // Update URL when tab changes
  const handleTabChange = (tab: BillingTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "subscription":
        return <SubscriptionTab />;
      case "plans":
        return <PlansTab />;
      case "invoices":
        return <InvoicesTab />;
      case "payments":
        return <PaymentsList />;
      case "usage":
        return <UsageTab />;
      default:
        return <SubscriptionTab />;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Billing</h1>
            <p className="mt-1 text-gray-500">Manage your subscription and billing settings</p>
          </div>
        </div>

        {/* Billing Layout with Vertical Tabs */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* Left Sidebar - Vertical Tab Navigation */}
          <div className="lg:col-span-1">
            <Card className="p-2">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <Button
                      key={tab.id}
                      variant="ghost"
                      className={cn(
                        "h-auto w-full justify-start gap-3 px-3 py-3",
                        isActive && "bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                      )}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <Icon
                        className={cn(
                          "h-5 w-5 flex-shrink-0",
                          isActive ? "text-blue-700" : "text-gray-500"
                        )}
                      />
                      <div className="flex flex-col items-start text-left">
                        <span
                          className={cn(
                            "text-sm font-medium",
                            isActive ? "text-blue-700" : "text-gray-900"
                          )}
                        >
                          {tab.label}
                        </span>
                        <span
                          className={cn(
                            "mt-0.5 text-xs",
                            isActive ? "text-blue-600" : "text-gray-500"
                          )}
                        >
                          {tab.description}
                        </span>
                      </div>
                    </Button>
                  );
                })}
              </nav>
            </Card>
          </div>

          {/* Right Content Area */}
          <div className="lg:col-span-3">{renderTabContent()}</div>
        </div>
      </div>
    </DashboardLayout>
  );
}
